import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { DataTableDirective } from 'angular-datatables';
import { FlashMessagesService } from 'flash-messages-angular';
import * as _ from 'lodash';
import { Subject } from 'rxjs';
import { DatatablesHelper } from 'src/app/helpers';
import { Property, PropertyChallenge, User } from 'src/app/models';
import { AdminService } from 'src/app/services/admin.service';
import { AuthService } from 'src/app/services/auth.service';

type UIChallenge = PropertyChallenge & {
  fromUser?: User;
  property?: Property;
};

@Component({
  selector: 'app-admin-properties-challenges',
  templateUrl: './admin-properties-challenges.component.html',
  styleUrls: ['./admin-properties-challenges.component.css']
})
export class AdminPropertiesChallengesComponent implements OnInit, OnDestroy {
  readonly COLS_HIDDEN_BY_DEFAULT = [9, 10, 11];

  challenges: UIChallenge[] = [];
  loaded = false;

  dtOptions: DataTables.Settings = {};

  // We use this trigger because fetching the list of users can take a while,
  // thus we ensure the data is fetched before rendering
  dtTrigger: Subject<any> = new Subject<any>();

  @ViewChild(DataTableDirective, { static: false })
  dtElement!: DataTableDirective;

  constructor(
    public auth: AuthService,
    private adminService: AdminService,
    private flashMessage: FlashMessagesService
  ) { }

  ngOnInit(): void {
    this.initTable();
    this.loadTable();
  }

  ngOnDestroy(): void {
    // Do not forget to unsubscribe the event
    this.dtTrigger.unsubscribe();
  }

  private initTable(): void {
    this.dtOptions = {
      pagingType: 'full_numbers',
      pageLength: 50,
      // enables horizontal scrolling on datatable
      scrollX: true,

      // Declare the use of the extension in the dom parameter and give a class for the css (can't get the css to apply though so commented out)
      // dom: '<"table-buttons"lBfr>tip',
      dom: 'lBfrtip',

      columnDefs: [
        // Indexes of columns to be hidden by default
        { targets: this.COLS_HIDDEN_BY_DEFAULT, visible: false }
      ],
    };

    // Configure the buttons
    Object.assign(this.dtOptions, {
      buttons: [
        'colvis',
        'excel'
      ]
    });
  }

  private async loadTable() {
    const query = {
      getusers:      true,
      getproperties: true,
    };

    const res: any = await this.adminService.getChallenges(query).toPromise();

    this.challenges = res.challenges;

    if (this.loaded) {
      this.rerender();
    }
    else {
      // Calling the DT trigger to manually render the table
      this.dtTrigger.next();
      this.loaded = true;
    }
  }

  private async tryReloadTable() {
    await this.loadTable().catch(() => {});
  }

  private rerender() {
    DatatablesHelper.redrawTable(this.dtElement, this.dtTrigger);
  }

  async deleteChallenge(index: number) {
    const challenge = this.challenges[index];

    // Delete Challenge
    const data: any = await this.adminService.deleteChallenge(challenge._id).toPromise();

    // Reload table data
    await this.tryReloadTable();

    // Success
    if (data.success) {
      // Display success
      this.flashMessage.show("Challenge deleted.", {
        cssClass: 'alert-success',
        timeout: 3000
      });
    }
  }

  sortDate(value: any) {
    return value ? +new Date(value) : 0;
  }
}
